/**
 * Select all / Deselect all for the multi-select popover.
 * For Media Devoted | Created By James Alexandrov
 *
 * Both act on the rows the search currently SHOWS, which is what makes them
 * useful ("uk" then Select all). The labels say so whenever a search is
 * narrowing the list, so "Select all" is never read as "all 250 countries"
 * while only three are on screen.
 */

import type { FC } from "react";

import type { SearchableSelectOption } from "../SearchableSelect";

const action =
  "rounded px-1 py-0.5 hover:underline disabled:pointer-events-none disabled:opacity-40";

interface Props {
  /** The options the search leaves visible. */
  shown: SearchableSelectOption<string>[];
  value: string[];
  onChange: (value: string[]) => void;
  /** Whether a search is narrowing the list. */
  searching: boolean;
}

export const MultiSelectBulkActions: FC<Props> = ({
  shown,
  value,
  onChange,
  searching,
}) => {
  const picked = new Set(value);
  const allShown = shown.length > 0 && shown.every((o) => picked.has(o.value));
  const anyShown = shown.some((o) => picked.has(o.value));
  const suffix = searching ? " matches" : "";

  const selectAll = () =>
    onChange([...new Set([...value, ...shown.map((o) => o.value)])]);
  /** Drops only what is on screen — a pick hidden by the search survives. */
  const deselectAll = () => {
    const onScreen = new Set(shown.map((o) => o.value));
    onChange(value.filter((v) => !onScreen.has(v)));
  };

  return (
    <div className="flex items-center gap-3 border-b border-border px-3 py-1.5 text-xs">
      <button
        type="button"
        onClick={selectAll}
        disabled={allShown}
        className={`${action} text-primary`}
      >
        Select all{suffix}
      </button>
      <button
        type="button"
        onClick={deselectAll}
        disabled={!anyShown}
        className={`${action} text-muted-foreground hover:text-foreground`}
      >
        Deselect all{suffix}
      </button>
      {value.length > 0 && (
        <span className="ml-auto text-muted-foreground">
          {value.length} selected
        </span>
      )}
    </div>
  );
};

export default MultiSelectBulkActions;
